import axios from 'axios';
import { API_BASE_URL, AUTH_API_URL } from '../config/env';
import {
  getAccessToken, setAccessToken, clearAccessToken,
  getCsrfToken,
  getRefreshingPromise, setRefreshingPromise, clearRefreshingPromise,
} from './tokenStore';

const axiosInstance = axios.create({
  withCredentials: true,
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: attach access token + CSRF from in-memory store / cookie ──
axiosInstance.interceptors.request.use(
  (config) => {
    const csrf = getCsrfToken();
    if (csrf) config.headers['x-csrf-token'] = csrf;

    const token = getAccessToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response: silently refresh on 401, then retry ──
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    const isCsrfMismatch = error.response?.status === 403 && error.response?.data?.message === "CSRF validation failed";

    // Only attempt refresh once per request and only for 401s or 403 CSRF mismatch
    if ((error.response?.status !== 401 && !isCsrfMismatch) || original._retried) {
      return Promise.reject(error);
    }
    original._retried = true;

    // Queue behind any in-flight refresh so we don't fire multiple refresh calls
    if (!getRefreshingPromise()) {
      const refreshPromise = (async () => {
        try {
          const csrf = getCsrfToken();
          const res = await axios.post(
            `${AUTH_API_URL}/refresh`,
            {},
            {
              withCredentials: true,
              headers: { ...(csrf ? { 'x-csrf-token': csrf } : {}) },
            }
          );
          setAccessToken(res.data.data.accessToken);
          if (res.data.data.csrfToken) {
            localStorage.setItem('csrf', res.data.data.csrfToken);
          }
        } catch (err) {
          // If the backend returns 409 Conflict, another tab just refreshed the session.
          // Wait 500ms and retry — the browser will automatically send the new refresh cookie!
          if (err.response?.status === 409) {
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const retryRes = await axios.post(
                `${AUTH_API_URL}/refresh`,
                {},
                {
                  withCredentials: true,
                  headers: { ...(getCsrfToken() ? { 'x-csrf-token': getCsrfToken() } : {}) },
                }
              );
              setAccessToken(retryRes.data.data.accessToken);
              if (retryRes.data.data.csrfToken) {
                localStorage.setItem('csrf', retryRes.data.data.csrfToken);
              }
              return; // Successfully recovered!
            } catch (retryErr) {
              // Retry failed, fall through to logout
            }
          }

          // Refresh itself failed — user must log in again
          clearAccessToken();
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('user');
          localStorage.removeItem('name');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          throw error;
        } finally {
          clearRefreshingPromise();
        }
      })();
      setRefreshingPromise(refreshPromise);
    }

    await getRefreshingPromise();

    // Retry original request with the new access token
    original.headers['Authorization'] = `Bearer ${getAccessToken()}`;
    return axiosInstance(original);
  }
);

export default axiosInstance;